import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AdminListCard,
  AdminListTable,
  ConfirmDeleteModal,
  useBatchSelection,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { usePaymentCreditsList, usePaymentCreditMutations } from "../api";
import { buildPaymentCreditBatchActions } from "./batchActions";
import type { PaymentCredit } from "../types";

const PAGE_SIZE = 25;

const List = (): JSX.Element => {
  const navigate = useNavigate();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");

  const sel = useBatchSelection([sourceFilter]);
  const { destroy } = usePaymentCreditMutations();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);

  const { rows, pageMeta, isLoading, isFetching, refetch } =
    usePaymentCreditsList({
      page: { number: pageNumber, size: pageSize },
      filter: sourceFilter ? { "payment-source": sourceFilter } : undefined,
    });

  const selected = useMemo(
    () => rows.filter((r) => sel.selectedIds.includes(r.id)),
    [rows, sel.selectedIds]
  );

  const columns: ColumnDef<PaymentCredit>[] = [
    {
      accessorKey: "paymentSource",
      header: "Source",
      cell: ({ row }) => row.original.paymentSource,
    },
    {
      accessorKey: "description",
      header: "Description",
      cell: ({ row }) => row.original.description,
    },
    {
      accessorKey: "criteria",
      header: "Criteria",
      cell: ({ row }) => row.original.criteria,
    },
  ];

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    const targets = [...selected];
    let failures = 0;
    for (const pc of targets) {
      try {
        await destroy(pc.id);
      } catch {
        failures += 1;
      }
    }
    setToast(
      failures === 0
        ? {
            variant: "success",
            text: `${targets.length} payment credit(s) deleted.`,
          }
        : {
            variant: "danger",
            text: `Failed on ${failures} of ${targets.length} payment credit(s).`,
          }
    );
    sel.clear();
    refetch();
  };

  const batchActions = buildPaymentCreditBatchActions(() =>
    setConfirmOpen(true)
  );

  const submitSearch = () => {
    setPageNumber(1);
    setSourceFilter(searchInput);
  };

  if (isLoading) return <ScopeStackSpinner />;

  return (
    <>
      <AdminListCard
        title="Payment Credits"
        onNew={() => navigate("new")}
        newButtonLabel="+ Add A Payment Credit"
        searchProps={{
          label: "Search Payment Credits",
          placeholder: "Search by source",
          value: searchInput,
          onChange: (e) => setSearchInput(e.target.value),
          onSubmit: (e) => {
            e.preventDefault();
            submitSearch();
          },
          onClear: () => {
            setSearchInput("");
            setPageNumber(1);
            setSourceFilter("");
          },
        }}
        batchActions={batchActions}
        selectedCount={sel.selectedIds.length}
      >
        <AdminListTable
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectedIds={sel.selectedIds}
          setSelectedIds={sel.setSelectedIds}
          onEdit={(id) => navigate(`${id}/edit`)}
          emptyMessage="No payment credits found."
          paginationEnabled
          pageSizeEnabled
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={(p) => {
            sel.clear();
            setPageNumber(p);
          }}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            sel.clear();
            setPageSize(size);
            setPageNumber(1);
          }}
        />
      </AdminListCard>

      <ConfirmDeleteModal
        open={confirmOpen}
        title="Delete payment credits?"
        message={`Are you sure you want to permanently delete ${sel.selectedIds.length} payment credit(s)? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />

      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default List;
